import { useCallback } from "react";

let sharedAudioContext = null;
let warningLogged = false;

function getAudioContext() {
	if (typeof window === "undefined") return null;
	const Ctx = window.AudioContext || window.webkitAudioContext;
	if (!Ctx) return null;
	if (!sharedAudioContext || sharedAudioContext.state === "closed") {
		sharedAudioContext = new Ctx();
	}
	if (sharedAudioContext.state === "suspended") {
		sharedAudioContext.resume().catch(() => void 0);
	}
	return sharedAudioContext;
}

function createTone({
	frequency = 440,
	duration = 0.12,
	type = "sine",
	gain = 0.075,
	attack = 0.008,
	release = 0.02,
	delay = 0
} = {}) {
	const ctx = getAudioContext();
	if (!ctx) {
		if (!warningLogged) {
			warningLogged = true;
			console.warn("AudioContext not available in this environment.");
		}
		return;
	}

	const now = ctx.currentTime + Math.max(0, delay);
	const safeDuration = Math.max(0.03, duration);
	const osc = ctx.createOscillator();
	const gainNode = ctx.createGain();

	osc.type = type;
	osc.frequency.setValueAtTime(Math.max(40, frequency), now);

	gainNode.gain.setValueAtTime(0.0001, now);
	gainNode.gain.linearRampToValueAtTime(Math.max(0.001, gain), now + attack);
	gainNode.gain.exponentialRampToValueAtTime(0.0001, now + safeDuration + release);

	osc.connect(gainNode);
	gainNode.connect(ctx.destination);

	osc.start(now);
	osc.stop(now + safeDuration + release);
}

function playSequence(steps = []) {
	steps.forEach((step) => createTone(step));
}

export function playClick() {
	playSequence([
		{ frequency: 780, duration: 0.045, type: "triangle", gain: 0.04, delay: 0 },
		{ frequency: 980, duration: 0.035, type: "triangle", gain: 0.03, delay: 0.04 }
	]);
}

export function playRouteChange() {
	playSequence([
		{ frequency: 420, duration: 0.045, type: "sine", gain: 0.03, delay: 0 },
		{ frequency: 520, duration: 0.05, type: "sine", gain: 0.035, delay: 0.05 },
		{ frequency: 640, duration: 0.055, type: "sine", gain: 0.038, delay: 0.11 }
	]);
}

export function playSuccess() {
	playSequence([
		{ frequency: 523, duration: 0.11, type: "triangle", gain: 0.065, delay: 0 },
		{ frequency: 659, duration: 0.11, type: "triangle", gain: 0.065, delay: 0.05 },
		{ frequency: 784, duration: 0.15, type: "triangle", gain: 0.07, delay: 0.1 }
	]);
}

export function playWarning() {
	playSequence([
		{ frequency: 560, duration: 0.1, type: "square", gain: 0.045, delay: 0 },
		{ frequency: 490, duration: 0.1, type: "square", gain: 0.045, delay: 0.12 }
	]);
}

export function playError() {
	playSequence([
		{ frequency: 380, duration: 0.13, type: "sawtooth", gain: 0.065, delay: 0 },
		{ frequency: 300, duration: 0.16, type: "sawtooth", gain: 0.07, delay: 0.11 },
		{ frequency: 220, duration: 0.22, type: "sawtooth", gain: 0.075, delay: 0.23 }
	]);
}

export function playInfo() {
	playSequence([
		{ frequency: 620, duration: 0.07, type: "sine", gain: 0.038, delay: 0 },
		{ frequency: 760, duration: 0.09, type: "sine", gain: 0.042, delay: 0.08 }
	]);
}

export function playNotify() {
	playSequence([
		{ frequency: 610, duration: 0.08, type: "triangle", gain: 0.05, delay: 0 },
		{ frequency: 820, duration: 0.08, type: "triangle", gain: 0.05, delay: 0.13 }
	]);
}

export function playRobotDetected() {
	playSequence([
		{ frequency: 440, duration: 0.08, type: "sawtooth", gain: 0.05, delay: 0 },
		{ frequency: 880, duration: 0.08, type: "sawtooth", gain: 0.05, delay: 0.09 },
		{ frequency: 1320, duration: 0.11, type: "sawtooth", gain: 0.055, delay: 0.2 }
	]);
}

export function playTravelStart() {
	createTone({ frequency: 200, duration: 0.22, type: "sawtooth", gain: 0.055 });
}

export function playTravelStop() {
	createTone({ frequency: 150, duration: 0.16, type: "sawtooth", gain: 0.055 });
}

export function playStartShift() {
	playSequence([
		{ frequency: 392, duration: 0.08, type: "triangle", gain: 0.05, delay: 0 },
		{ frequency: 494, duration: 0.08, type: "triangle", gain: 0.055, delay: 0.08 },
		{ frequency: 587, duration: 0.1, type: "triangle", gain: 0.06, delay: 0.16 }
	]);
}

export function playStopShift() {
	playSequence([
		{ frequency: 523, duration: 0.09, type: "triangle", gain: 0.05, delay: 0 },
		{ frequency: 415, duration: 0.1, type: "triangle", gain: 0.055, delay: 0.09 },
		{ frequency: 311, duration: 0.12, type: "triangle", gain: 0.06, delay: 0.2 }
	]);
}

export function playMelding() {
	playSequence([
		{ frequency: 523, duration: 0.08, type: "triangle", gain: 0.055, delay: 0 },
		{ frequency: 659, duration: 0.1, type: "triangle", gain: 0.06, delay: 0.08 },
		{ frequency: 880, duration: 0.14, type: "triangle", gain: 0.06, delay: 0.18 }
	]);
}

export function useSound() {
	const stablePlayClick = useCallback(() => playClick(), []);
	const stablePlayRouteChange = useCallback(() => playRouteChange(), []);
	const stablePlaySuccess = useCallback(() => playSuccess(), []);
	const stablePlayWarning = useCallback(() => playWarning(), []);
	const stablePlayError = useCallback(() => playError(), []);
	const stablePlayInfo = useCallback(() => playInfo(), []);
	const stablePlayNotify = useCallback(() => playNotify(), []);
	const stablePlayRobotDetected = useCallback(() => playRobotDetected(), []);
	const stablePlayTravelStart = useCallback(() => playTravelStart(), []);
	const stablePlayTravelStop = useCallback(() => playTravelStop(), []);
	const stablePlayStartShift = useCallback(() => playStartShift(), []);
	const stablePlayStopShift = useCallback(() => playStopShift(), []);
	const stablePlayMelding = useCallback(() => playMelding(), []);

	return {
		playClick: stablePlayClick,
		playRouteChange: stablePlayRouteChange,
		playSuccess: stablePlaySuccess,
		playWarning: stablePlayWarning,
		playError: stablePlayError,
		playInfo: stablePlayInfo,
		playNotify: stablePlayNotify,
		playRobotDetected: stablePlayRobotDetected,
		playTravelStart: stablePlayTravelStart,
		playTravelStop: stablePlayTravelStop,
		playStartShift: stablePlayStartShift,
		playStopShift: stablePlayStopShift,
		playMelding: stablePlayMelding
	};
}